import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import t from 'format-message';
import React, { useState, useRef } from 'react';
import { IconButton } from '@instructure/ui-buttons';
import { Flex } from '@instructure/ui-flex';
import { IconAttachMediaLine, IconCodeLine, IconDocumentLine, IconFolderLine, IconFolderLockedLine, IconImageLine, IconMsExcelLine, IconMsPptLine, IconPaperclipLine, IconPdfLine, IconXSolid, IconZippedLine } from '@instructure/ui-icons';
import { Modal } from '@instructure/ui-modal';
import { Spinner } from '@instructure/ui-spinner';
import { Text } from '@instructure/ui-text';
import { Tooltip } from '@instructure/ui-tooltip';
import { TruncateText } from '@instructure/ui-truncate-text';
import { View } from '@instructure/ui-view';
var MIME_ICONS = {
  audio: /*#__PURE__*/React.createElement(IconAttachMediaLine, {
    title: t('Audio File')
  }),
  code: /*#__PURE__*/React.createElement(IconCodeLine, {
    title: t('Code File')
  }),
  doc: /*#__PURE__*/React.createElement(IconDocumentLine, {
    title: t('Doc File')
  }),
  'folder-locked': /*#__PURE__*/React.createElement(IconFolderLockedLine, {
    title: t('Folder Locked')
  }),
  folder: /*#__PURE__*/React.createElement(IconFolderLine, {
    title: t('Folder')
  }),
  html: /*#__PURE__*/React.createElement(IconCodeLine, {
    title: t('HTML File')
  }),
  image: /*#__PURE__*/React.createElement(IconImageLine, {
    title: t('Image File')
  }),
  pdf: /*#__PURE__*/React.createElement(IconPdfLine, {
    title: t('PDF File')
  }),
  ppt: /*#__PURE__*/React.createElement(IconMsPptLine, {
    title: t('PowerPoint File')
  }),
  text: /*#__PURE__*/React.createElement(IconDocumentLine, {
    title: t('Text File')
  }),
  video: /*#__PURE__*/React.createElement(IconAttachMediaLine, {
    title: t('Video File')
  }),
  xls: /*#__PURE__*/React.createElement(IconMsExcelLine, {
    title: t('Excel File')
  }),
  zip: /*#__PURE__*/React.createElement(IconZippedLine, {
    title: t('Zip File')
  })
};
var DEFAULT_FILE_ICON = /*#__PURE__*/React.createElement(IconPaperclipLine, {
  title: t('File')
});
function getFileIcon(attachment) {
  var _MIME_ICONS$attachmen;
  if (attachment.mime_class === 'image' && attachment.thumbnail_url) {
    return /*#__PURE__*/React.createElement("img", {
      alt: t('{ filename } preview', {
        filename: attachment.display_name
      }),
      src: attachment.thumbnail_url,
      style: {
        height: '3rem',
        width: '3rem'
      }
    });
  }
  return attachment.mime_class ? (_MIME_ICONS$attachmen = MIME_ICONS[attachment.mime_class]) !== null && _MIME_ICONS$attachmen !== void 0 ? _MIME_ICONS$attachmen : DEFAULT_FILE_ICON : DEFAULT_FILE_ICON;
}
export var RemovableItem = function RemovableItem(_ref) {
  var onRemove = _ref.onRemove,
    screenReaderLabel = _ref.screenReaderLabel,
    childrenAriaLabel = _ref.childrenAriaLabel,
    children = _ref.children;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showRemove = _useState2[0],
    setShowRemove = _useState2[1];
  var blurTimeout = useRef(null);
  var handleInteraction = function handleInteraction() {
    if (blurTimeout.current) clearTimeout(blurTimeout.current);
    setShowRemove(true);
  };
  var handleExit = function handleExit() {
    blurTimeout.current = setTimeout(function () {
      return setShowRemove(false);
    });
  };
  return /*#__PURE__*/React.createElement(View, {
    position: "relative",
    display: "inline-block"
  }, /*#__PURE__*/React.createElement(View, {
    onMouseEnter: handleInteraction,
    onMouseLeave: handleExit,
    onFocus: handleInteraction,
    onBlur: handleExit,
    tabIndex: 0,
    role: "button",
    "aria-label": childrenAriaLabel,
    "data-testid": "removable-item"
  }, children), showRemove && /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      right: 0,
      top: 0
    }
  }, /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    shape: "circle",
    screenReaderLabel: screenReaderLabel,
    onClick: onRemove,
    onMouseEnter: handleInteraction,
    onMouseLeave: handleExit,
    onFocus: handleInteraction,
    onBlur: handleExit,
    "data-testid": "remove-button"
  }, /*#__PURE__*/React.createElement(IconXSolid, null))));
};
export var AttachmentItem = function AttachmentItem(_ref2) {
  var _attachment$display_n;
  var attachment = _ref2.attachment,
    onReplace = _ref2.onReplace,
    onDelete = _ref2.onDelete;
  var inputRef = useRef(null);
  if (attachment.isLoading) {
    return /*#__PURE__*/React.createElement(Spinner, {
      renderTitle: t('Loading'),
      size: "medium"
    });
  }
  var displayName = (_attachment$display_n = attachment.display_name) !== null && _attachment$display_n !== void 0 ? _attachment$display_n : attachment.id;
  return /*#__PURE__*/React.createElement(RemovableItem, {
    onRemove: onDelete,
    screenReaderLabel: t('Remove Attachment'),
    childrenAriaLabel: t('Replace { name } button', {
      name: displayName
    })
  }, /*#__PURE__*/React.createElement(Flex, {
    direction: "column",
    padding: "xx-small small",
    width: "80px"
  }, /*#__PURE__*/React.createElement(Flex.Item, {
    margin: "none xx-small xxx-small xx-small",
    align: "center"
  }, getFileIcon(attachment)), /*#__PURE__*/React.createElement(Flex.Item, null, /*#__PURE__*/React.createElement(Text, {
    size: "small"
  }, /*#__PURE__*/React.createElement(TruncateText, {
    position: "middle",
    maxLines: 3
  }, displayName)))), /*#__PURE__*/React.createElement("input", {
    "data-testid": "replacement-input",
    ref: inputRef,
    type: "file",
    style: {
      display: 'none'
    },
    "aria-hidden": true,
    onChange: onReplace
  }));
};
export var AttachmentDisplay = function AttachmentDisplay(_ref3) {
  var attachments = _ref3.attachments,
    onDeleteItem = _ref3.onDeleteItem,
    onReplaceItem = _ref3.onReplaceItem;
  return /*#__PURE__*/React.createElement(Flex, {
    alignItems: "start",
    wrap: "wrap"
  }, attachments.map(function (a) {
    return /*#__PURE__*/React.createElement(Flex.Item, {
      key: a.id
    }, /*#__PURE__*/React.createElement(AttachmentItem, {
      attachment: a,
      onDelete: function onDelete() {
        return onDeleteItem(a.id);
      },
      onReplace: function onReplace(e) {
        return onReplaceItem(a.id, e);
      }
    }));
  }));
};
export var FileAttachmentUpload = function FileAttachmentUpload(_ref4) {
  var onAddItem = _ref4.onAddItem;
  var inputRef = useRef(null);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Tooltip, {
    renderTip: t('Add an attachment'),
    placement: "top"
  }, /*#__PURE__*/React.createElement(IconButton, {
    screenReaderLabel: t('Add an attachment'),
    onClick: function onClick() {
      var _inputRef$current;
      return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.click();
    },
    margin: "xx-small",
    "data-testid": "attachment-upload"
  }, /*#__PURE__*/React.createElement(IconPaperclipLine, null))), /*#__PURE__*/React.createElement("input", {
    "data-testid": "attachment-input",
    ref: inputRef,
    type: "file",
    style: {
      display: 'none'
    },
    "aria-hidden": true,
    onChange: onAddItem,
    multiple: true
  }));
};
export var AttachmentUploadSpinner = function AttachmentUploadSpinner(_ref5) {
  var sendMessage = _ref5.sendMessage,
    isMessageSending = _ref5.isMessageSending,
    pendingUploads = _ref5.pendingUploads,
    sendArgs = _ref5.sendArgs;
  var label = t('Uploading Files');
  return /*#__PURE__*/React.createElement(Modal, {
    open: isMessageSending && pendingUploads.length > 0,
    label: label,
    shouldCloseOnDocumentClick: false,
    onExited: function onExited() {
      if (sendArgs) sendMessage(sendArgs);
    }
  }, /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(Flex, {
    direction: "column",
    textAlign: "center"
  }, /*#__PURE__*/React.createElement(Flex.Item, null, /*#__PURE__*/React.createElement(Spinner, {
    renderTitle: label,
    size: "large"
  })), /*#__PURE__*/React.createElement(Flex.Item, null, /*#__PURE__*/React.createElement(Text, null, t('Please wait while we upload attachments.'))))));
};